import { useState } from 'react';
import { formatCurrency } from '../../../utils/formatCurrency';
import { useTaxCreditPurchase } from '../../../hooks/purchases/useTaxCreditPurchase';
import SvgIcon from '../../SvgIcon/SvgIcon';

const TaxCreditTotals = ({
  form,
  readOnly = false,
}: {
  form: any;
  readOnly?: boolean;
}) => {
  const { handlerUpdatePerceivedIVA } = useTaxCreditPurchase();

  const [isEditingPerceived, setIsEditingPerceived] = useState(false);
  const [perceivedIVADraft, setPerceivedIVADraft] = useState('');

  const subtotal = Number(form.taxableSubtotal) || 0;
  const calculatedIVA = Number(form.calculatedIVA) || 0;
  const withheldIVA = Number(form.withheldIVA) || 0;
  const perceivedIVA = Number(form.perceivedIVA) || 0;
  const total = Number(form.total) || 0; // ✅ ahora viene desde backend

  const handleStartEditPerceived = () => {
    setPerceivedIVADraft(perceivedIVA ? String(perceivedIVA) : '0');
    setIsEditingPerceived(true);
  };

  const handleCancelEditPerceived = () => {
    setIsEditingPerceived(false);
  };

  const handleSavePerceived = async () => {
    const parsed = parseFloat(perceivedIVADraft);
    if (isNaN(parsed) || parsed < 0 || !form.id) {
      return;
    }

    const success = await handlerUpdatePerceivedIVA(form.id, parsed);
    if (success) {
      setIsEditingPerceived(false);
    }
  };

  return (
    <div className="w-full max-w-md space-y-4 text-sm uppercase">
      <p className="flex justify-between text-gray-700 dark:text-gray-300 font-medium">
        <span>Subtotal</span>
        <span>${formatCurrency(subtotal)}</span>
      </p>
      <p className="flex justify-between text-gray-700 dark:text-gray-300 font-medium">
        <span>IVA 13%</span>
        <span>${formatCurrency(calculatedIVA)}</span>
      </p>

      {(perceivedIVA > 0 || isEditingPerceived) && (
        <div className="flex justify-between items-center text-gray-700 dark:text-gray-300 font-medium normal-case">
          <span className="uppercase">IVA Percibido</span>
          {isEditingPerceived && !readOnly ? (
            <div className="flex items-center gap-1">
              <input
                type="number"
                step="0.01"
                min="0"
                autoFocus
                value={perceivedIVADraft}
                onChange={(e) => setPerceivedIVADraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSavePerceived();
                  if (e.key === 'Escape') handleCancelEditPerceived();
                }}
                className="w-24 rounded border border-stroke dark:border-strokedark bg-transparent px-2 py-1 text-right text-sm normal-case"
              />
              <button
                type="button"
                title="Guardar"
                onClick={handleSavePerceived}
                className="text-primary hover:opacity-80"
              >
                <SvgIcon name="check" width={16} height={16} />
              </button>
              <button
                type="button"
                title="Cancelar"
                onClick={handleCancelEditPerceived}
                className="text-gray-400 hover:opacity-80"
              >
                <SvgIcon name="close" width={16} height={16} />
              </button>
            </div>
          ) : (
            <span className="flex items-center gap-1">
              ${formatCurrency(perceivedIVA)}
              {!readOnly && (
                <button
                  type="button"
                  title="Editar IVA Percibido manualmente"
                  onClick={handleStartEditPerceived}
                  className="text-gray-400 hover:text-primary"
                >
                  <SvgIcon name="edit" width={14} height={14} />
                </button>
              )}
            </span>
          )}
        </div>
      )}

      {perceivedIVA === 0 && !isEditingPerceived && !readOnly && (
        <div className="flex justify-end -mt-2">
          <button
            type="button"
            onClick={handleStartEditPerceived}
            className="text-xs text-gray-400 hover:text-primary normal-case"
          >
            + Agregar IVA Percibido manual
          </button>
        </div>
      )}

      {withheldIVA > 0 && (
        <p className="flex justify-between text-gray-700 dark:text-gray-300 font-medium">
          <span>IVA Retenido</span>
          <span>${formatCurrency(withheldIVA)}</span>
        </p>
      )}
      <p className="flex justify-between border-t pt-4 font-semibold text-gray-900 dark:text-white dark:border-strokedark">
        <span>Total a Pagar</span>
        <span>${formatCurrency(total)}</span>
      </p>
    </div>
  );
};

export default TaxCreditTotals;
